// @tennis-matches/services/supabase
// Supabase client service
// Migrated from src/services/supabase/supabase.service.js

let _clientPromise = null;

/** Sync-read bearer for custom fetch: avoids await getSession() on every request (major perf win). */
let __supabaseAuthBearerCache = "";

const SUPABASE_USER_CACHE_MS = 15000;
/** @type {{ user: import('@supabase/supabase-js').User | null, token: string, ts: number } | null} */
let __supabaseUserCache = null;
/** @type {Promise<import('@supabase/supabase-js').AuthResponse> | null} */
let __authGetUserInflight = null;
/** @type {((jwt?: string) => Promise<import('@supabase/supabase-js').AuthResponse>) | null} */
let __originalAuthGetUser = null;

export function invalidateSupabaseUserCache() {
  __supabaseUserCache = null;
  __authGetUserInflight = null;
}

import { createAuthPersistenceStorage } from "../auth/auth-persistence.js";

/** Optional: React Native setzt AsyncStorage via initMobileRuntime (apps/mobile). */
let __configuredAuthStorage = null;
/** Web: localStorage vs. sessionStorage je nach „Angemeldet bleiben“. */
let __webAuthPersistenceStorage = null;

export function setSupabaseAuthStorage(storage) {
  __configuredAuthStorage = storage || null;
}

function __resolveAuthStorageSync() {
  if (__configuredAuthStorage) return __configuredAuthStorage;
  try {
    const g = globalThis.__TTB_SUPABASE_AUTH_STORAGE__;
    if (g) return g;
  } catch (_) {}
  try {
    if (typeof window !== "undefined") {
      if (!__webAuthPersistenceStorage) {
        __webAuthPersistenceStorage = createAuthPersistenceStorage();
      }
      return __webAuthPersistenceStorage;
    }
  } catch (_) {
    // noop
  }
  return undefined;
}

function __isSupabaseAuthSessionInvalidError(error) {
  if (!error || typeof error !== "object") return false;
  const status = Number(/** @type {{ status?: number }} */ (error).status);
  if (status === 401 || status === 403) return true;
  const msg = String(
    /** @type {{ message?: string }} */ (error).message || "",
  ).toLowerCase();
  return (
    msg.includes("invalid jwt") ||
    msg.includes("jwt expired") ||
    (msg.includes("session") && msg.includes("invalid"))
  );
}

async function __clearInvalidSupabaseAuthSessionLocal() {
  try {
    const supabase = globalThis.__ttb_supabase || (await getSupabaseClient());
    if (supabase?.auth) {
      const { data, error } = await ttbRunSerializedAuthGetSession(supabase);
      if (!error && data?.session?.access_token) {
        __setSupabaseAuthBearerFromSession(data.session);
        return;
      }
      if (supabase.auth.signOut) {
        await supabase.auth.signOut({ scope: "local" });
      }
    }
  } catch (_) {}
  __setSupabaseAuthBearerFromSession(null);
  invalidateSupabaseUserCache();
}

async function __cachedAuthGetUser(jwt) {
  if (!__originalAuthGetUser) {
    await getSupabaseClient();
  }
  if (!__originalAuthGetUser) {
    return { data: { user: null }, error: { message: "auth-not-ready" } };
  }

  const explicitJwt =
    jwt !== undefined && jwt !== null ? String(jwt).trim() : "";
  if (explicitJwt) {
    const res = await __originalAuthGetUser(jwt);
    if (res?.error && __isSupabaseAuthSessionInvalidError(res.error)) {
      await __clearInvalidSupabaseAuthSessionLocal();
      return { data: { user: null }, error: null };
    }
    return res;
  }

  const token = __supabaseAuthBearerCache || "";
  const now = Date.now();

  if (!token) {
    return { data: { user: null }, error: null };
  }

  if (
    __supabaseUserCache &&
    __supabaseUserCache.token === token &&
    now - __supabaseUserCache.ts < SUPABASE_USER_CACHE_MS
  ) {
    return { data: { user: __supabaseUserCache.user }, error: null };
  }

  if (__authGetUserInflight) {
    return __authGetUserInflight;
  }

  __authGetUserInflight = (async () => {
    const res = await __originalAuthGetUser();
    if (res?.error && __isSupabaseAuthSessionInvalidError(res.error)) {
      await __clearInvalidSupabaseAuthSessionLocal();
      return { data: { user: null }, error: null };
    }
    const user = res?.error ? null : (res?.data?.user ?? null);
    __supabaseUserCache = { user, token, ts: Date.now() };
    return res;
  })().finally(() => {
    __authGetUserInflight = null;
  });

  return __authGetUserInflight;
}

/**
 * Serialisiert `supabase.auth.getSession()` über alle Aufrufer (Client-Init, Bootstrap, Recovery).
 * Nur als zusätzlicher In-Tab-Schutz; Hauptmaßnahme gegen Cross-Tab-`AbortError` ist der
 * eigene `lock` (siehe `__ttbInProcessLock` in `__createClient`).
 */
let __authGetSessionSerialTail = Promise.resolve();

export function ttbRunSerializedAuthGetSession(supabase) {
  const p = __authGetSessionSerialTail.then(() => supabase.auth.getSession());
  __authGetSessionSerialTail = p.then(
    () => {},
    () => {},
  );
  return p;
}

/**
 * In-Process-Lock für Supabase Auth (`auth.lock`).
 *
 * Standard-Lock von `@supabase/auth-js` ist `navigatorLock` (Web Locks API).
 * Bei mehreren Tabs derselben Origin (z. B. mehrere lokale Apps unter `localhost`)
 * konkurrieren die Tabs um denselben Lock; nach `lockAcquireTimeout` (Default 10s)
 * wirft Safari `AbortError: The operation was aborted.` — genau das, was im Bootstrap
 * als „Sitzungsprüfung ist fehlgeschlagen" sichtbar wird.
 *
 * Diese Implementierung serialisiert nur **innerhalb** des aktuellen Tabs (Promise-Kette
 * pro Lock-Name) und verzichtet auf Cross-Tab-Sperren. Token-Refresh ist im laufenden
 * Betrieb selten genug, dass eine Cross-Tab-Race nicht praxisrelevant ist.
 */
const __ttbInProcessLockTails = Object.create(null);

function __ttbInProcessLock(name, _acquireTimeoutMs, fn) {
  const key = String(name || "default");
  const previous = __ttbInProcessLockTails[key] || Promise.resolve();
  const next = previous.then(
    () => fn(),
    () => fn(),
  );
  __ttbInProcessLockTails[key] = next.then(
    () => {},
    () => {},
  );
  return next;
}

function __setSupabaseAuthBearerFromSession(session) {
  const token = String(session?.access_token || "").trim();
  if (token !== __supabaseAuthBearerCache) {
    invalidateSupabaseUserCache();
  }
  __supabaseAuthBearerCache = token;
}

/** Nach signIn/signUp sofort JWT-Cache setzen (sonst Race vor dem nächsten RPC). */
export function syncSupabaseAuthBearerFromSession(session) {
  __setSupabaseAuthBearerFromSession(session || null);
}

/** JWT aus dem In-Tab-Cache (gleiche Quelle wie Supabase-PostgREST-Fetch). */
export function getSupabaseAuthBearerTokenSync() {
  return String(__supabaseAuthBearerCache || "").trim();
}

/** Stellt sicher, dass der Sync-Bearer-Cache die aktuelle Session trägt. */
export async function hydrateSupabaseAuthBearerCache() {
  const supabase = await getSupabaseClient();
  const { data } = await ttbRunSerializedAuthGetSession(supabase);
  __setSupabaseAuthBearerFromSession(data?.session || null);
  return Boolean(String(data?.session?.access_token || "").trim());
}

/** @type {Set<(event: string, session: import('@supabase/supabase-js').Session | null) => void>} */
const __authStateSubscribers = new Set();
let __authStateHubRegistered = false;

function __dispatchAuthStateChange(event, session) {
  __setSupabaseAuthBearerFromSession(session);
  for (const cb of __authStateSubscribers) {
    try {
      cb(event, session);
    } catch (_) {
      // subscriber errors must not break auth hub
    }
  }
}

function __registerAuthStateHub(supabase) {
  if (__authStateHubRegistered || !supabase?.auth) return;
  __authStateHubRegistered = true;
  supabase.auth.onAuthStateChange((event, session) => {
    __dispatchAuthStateChange(event, session);
  });
}

/**
 * Ein zentraler Auth-State-Hub statt mehrfacher `supabase.auth.onAuthStateChange`-Listener
 * (verhindert StrictMode/HMR-Leaks und 4×-Logs pro Event).
 * @param {(event: string, session: import('@supabase/supabase-js').Session | null) => void} callback
 * @returns {() => void} unsubscribe
 */
export function subscribeSupabaseAuthStateChange(callback) {
  if (typeof callback !== "function") return () => {};

  __authStateSubscribers.add(callback);

  if (globalThis.__ttb_supabase) {
    __registerAuthStateHub(globalThis.__ttb_supabase);
  } else {
    void getSupabaseClient().then((supabase) => {
      __registerAuthStateHub(supabase);
    });
  }

  return () => {
    __authStateSubscribers.delete(callback);
  };
}

async function __importSupabase() {
  return import("@supabase/supabase-js");
}
// --- internal helpers -------------------------------------------------------

function __runtimeEnv() {
  try {
    if (typeof globalThis !== "undefined" && globalThis.__TTB_ENV__) {
      return globalThis.__TTB_ENV__;
    }
    return typeof window !== "undefined" && window.__TTB_ENV__
      ? window.__TTB_ENV__
      : null;
  } catch (_) {
    return null;
  }
}

function __viteEnv() {
  try {
    // Works in Vite ESM. If not available, throws -> handled.
    return import.meta && import.meta.env ? import.meta.env : null;
  } catch (_) {
    return null;
  }
}

function __debugSupabaseVerbose() {
  try {
    const ve = __viteEnv();
    if (!ve?.DEV) return false;
    return (
      typeof localStorage !== "undefined" &&
      localStorage.getItem("DEBUG_SUPABASE") === "1"
    );
  } catch (_) {
    return false;
  }
}

function __devSupabaseFetchLog() {
  try {
    if (typeof __DEV__ !== "undefined" && __DEV__) return true;
    return !!__viteEnv()?.DEV;
  } catch (_) {
    return false;
  }
}

/** Nur pathname für Logs — keine Querystrings mit Tokens ausgeben. */
function __requestPathForLog(input) {
  try {
    const raw =
      typeof input === "string"
        ? input
        : input && typeof input.url === "string"
          ? input.url
          : "";
    if (!raw) return "";
    const u = new URL(raw, "http://localhost");
    return String(u.pathname || "") || raw.split("?")[0].slice(0, 120);
  } catch (_) {
    return "(unparsed)";
  }
}

function __isSupabaseDataRequest(input) {
  try {
    const raw =
      typeof input === "string"
        ? input
        : input && typeof input.url === "string"
          ? input.url
          : "";
    if (!raw) return false;
    const u = new URL(raw, "http://localhost");
    const p = String(u.pathname || "").toLowerCase();
    return (
      p.includes("/rest/v1/") ||
      p.includes("/rpc/") ||
      p.includes("/storage/v1/") ||
      p.includes("/functions/v1/") ||
      p.includes("/graphql/v1")
    );
  } catch (_) {
    return false;
  }
}

/** GoTrue REST (Session/User/Token) — siehe injectedFetch (AbortSignal für Safari). */
function __isSupabaseAuthV1Request(input) {
  try {
    const raw =
      typeof input === "string"
        ? input
        : input && typeof input.url === "string"
          ? input.url
          : "";
    if (!raw) return false;
    const u = new URL(raw, "http://localhost");
    return String(u.pathname || "")
      .toLowerCase()
      .includes("/auth/v1/");
  } catch (_) {
    return false;
  }
}

/** Öffentliche Supabase-URL/Anon-Key (Browser-Runtime, Vite-Env oder Fallback). */
export function readSupabaseClientConfig() {
  return __readSupabaseConfig();
}

const __SUPABASE_FALLBACK = Object.freeze({
  url: "https://jtvsamrxarxwinykyvpg.supabase.co",
  anonKey:
    "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imp0dnNhbXJ4YXJ4d2lueWt5dnBnIiwicm9sZSI6ImFub24iLCJpYXQiOjE3Njc0MjY2MjUsImV4cCI6MjA4MzAwMjYyNX0._p28PVt8Ws4fRC5uOgT5D4HzZfZAxtvJG_9sDhkXfZE",
});

function __isPlausibleSupabaseProjectUrl(rawUrl) {
  try {
    const u = new URL(String(rawUrl || "").trim());
    if (u.pathname && u.pathname !== "/") return false;
    const host = u.hostname.toLowerCase();
    if (
      host === "localhost" ||
      host === "127.0.0.1" ||
      host.endsWith(".local")
    ) {
      return false;
    }
    return host.endsWith(".supabase.co") || host.endsWith(".supabase.in");
  } catch (_) {
    return false;
  }
}

function __warnInvalidSupabaseUrl(scope, rawUrl) {
  try {
    if (!__devSupabaseFetchLog()) return;
    const value = String(rawUrl || "").trim();
    if (!value) return;
    console.warn(
      `[supabase] Ignoring invalid ${scope} SUPABASE_URL (must be https://*.supabase.co, not app origin):`,
      value,
    );
  } catch (_) {}
}

function __pickSupabaseCredentials(rt, ve) {
  const rtUrl = String(rt?.SUPABASE_URL || "").trim();
  const rtKey = String(rt?.SUPABASE_ANON_KEY || "").trim();
  if (rtUrl && rtKey && __isPlausibleSupabaseProjectUrl(rtUrl)) {
    return { url: rtUrl, anonKey: rtKey, source: "window.__TTB_ENV__" };
  }
  if (rtUrl && !__isPlausibleSupabaseProjectUrl(rtUrl)) {
    __warnInvalidSupabaseUrl("runtime", rtUrl);
  }

  const veUrl = String(ve?.VITE_SUPABASE_URL || "").trim();
  const veKey = String(ve?.VITE_SUPABASE_ANON_KEY || "").trim();
  if (veUrl && veKey && __isPlausibleSupabaseProjectUrl(veUrl)) {
    return { url: veUrl, anonKey: veKey, source: "import.meta.env" };
  }
  if (veUrl && !__isPlausibleSupabaseProjectUrl(veUrl)) {
    __warnInvalidSupabaseUrl("vite", veUrl);
  }

  return {
    url: __SUPABASE_FALLBACK.url,
    anonKey: __SUPABASE_FALLBACK.anonKey,
    source: "hard-fallback",
  };
}

function __supabaseConfigMatches(a, b) {
  return (
    String(a?.url || "").trim() === String(b?.url || "").trim() &&
    String(a?.anonKey || "").trim() === String(b?.anonKey || "").trim()
  );
}

function __resetSupabaseClientCache() {
  globalThis.__ttb_supabase = undefined;
  globalThis.__ttb_supabase_cfg = undefined;
  _clientPromise = null;
  __authStateHubRegistered = false;
  __originalAuthGetUser = null;
  invalidateSupabaseUserCache();
}

/** Vor OAuth-Redirect: frischer Client + kein veralteter PKCE-State aus anderem Supabase-Projekt. */
export function resetSupabaseClientForAuthRedirect() {
  __resetSupabaseClientCache();
}

function __readSupabaseConfig() {
  const rt = __runtimeEnv() || {};
  const ve = __viteEnv() || {};
  const picked = __pickSupabaseCredentials(rt, ve);

  const phase1TenantId =
    (rt.PHASE1_TENANT_ID && String(rt.PHASE1_TENANT_ID).trim()) ||
    (rt.VITE_PHASE1_TENANT_ID && String(rt.VITE_PHASE1_TENANT_ID).trim()) ||
    (ve.VITE_PHASE1_TENANT_ID && String(ve.VITE_PHASE1_TENANT_ID).trim()) ||
    (() => {
      try {
        return typeof window !== "undefined" && window.localStorage
          ? String(
              window.localStorage.getItem("ttb_active_tenant_id") || "",
            ).trim()
          : "";
      } catch (_) {
        return "";
      }
    })();

  return {
    url: picked.url,
    anonKey: picked.anonKey,
    phase1TenantId,
    source: picked.source,
  };
}

function __hasOAuthPkceCodeInBrowserUrl() {
  if (typeof window === "undefined") return false;
  try {
    const u = new URL(window.location.href);
    return u.searchParams.has("code");
  } catch (_) {
    return false;
  }
}

async function __createClient() {
  const cfg = __readSupabaseConfig();

  const url = String(cfg.url || "").trim();
  const key = String(cfg.anonKey || "").trim();

  if (!url || !key) {
    // should never happen due to hard fallback, but keep message stable
    throw new Error("Supabase client not configured");
  }

  const { createClient } = await __importSupabase();
  const injectedFetch = (input, init = {}) => {
    const fetchInit = { ...init };
    // Safari/WebKit: Supabase Auth übergibt häufig ein AbortSignal; parallele Starts (Init + Bootstrap)
    // können sich gegenseitig abbrechen. Für /auth/v1/* ohne Signal — Timeouts bleiben serverseitig.
    if (
      __isSupabaseAuthV1Request(input) &&
      Object.prototype.hasOwnProperty.call(fetchInit, "signal")
    ) {
      delete fetchInit.signal;
    }

    const headers = new Headers(fetchInit?.headers || {});
    const hadIncomingAuth = headers.has("Authorization");
    const isDataRequest = __isSupabaseDataRequest(input);
    // Always provide project anon key for PostgREST/RPC access.
    headers.set("apikey", key);
    const hasApikey = headers.has("apikey");
    // Session bearer from cache (updated via onAuthStateChange + one-time hydrate); fallback anon.
    // Kein await getSession() — nur synchroner Cache (siehe Performance).
    if (!headers.has("Authorization") && isDataRequest) {
      const sessionToken = String(__supabaseAuthBearerCache || "").trim();
      headers.set("Authorization", `Bearer ${sessionToken || key}`);
    }
    const method = String(fetchInit?.method || "GET").toUpperCase();
    const pathname = __requestPathForLog(input);
    const cachedTok = String(__supabaseAuthBearerCache || "").trim();
    const bearerSource = hadIncomingAuth
      ? "incoming-header"
      : !isDataRequest
        ? "none-non-data-endpoint"
        : cachedTok && cachedTok !== key
          ? "session-cache-jwt"
          : "anon-key-bearer";
    const logFetch = __devSupabaseFetchLog();
    const t0 =
      logFetch && typeof performance !== "undefined" ? performance.now() : 0;

    return fetch(input, { ...fetchInit, headers })
      .then((res) => {
        if (logFetch && typeof console !== "undefined" && console.debug) {
          const ms =
            typeof performance !== "undefined" ? performance.now() - t0 : 0;
          console.debug("[perf][supabase-fetch]", {
            pathname,
            method,
            status: res.status,
            ms: Math.round(ms * 10) / 10,
            apikeyHeader: hasApikey,
            authorizationBearerSource: bearerSource,
          });
        }
        if (
          logFetch &&
          res.status === 404 &&
          __isSupabaseAuthV1Request(input) &&
          typeof console !== "undefined" &&
          console.warn
        ) {
          try {
            const raw =
              typeof input === "string"
                ? input
                : input && typeof input.url === "string"
                  ? input.url
                  : "";
            const host = raw ? new URL(raw, "http://localhost").hostname : "";
            if (host === "localhost" || host === "127.0.0.1") {
              console.warn(
                "[supabase] Auth request hit the Vite dev server (404). Check window.__TTB_ENV__.SUPABASE_URL — expected https://*.supabase.co. Hard reload if config changed.",
                { requestUrl: raw, resolvedUrl: url },
              );
            }
          } catch (_) {}
        }
        return res;
      })
      .catch((err) => {
        if (logFetch && typeof console !== "undefined" && console.debug) {
          const ms =
            typeof performance !== "undefined" ? performance.now() - t0 : 0;
          console.debug("[perf][supabase-fetch]", {
            pathname,
            method,
            status: "network-error",
            ms: Math.round(ms * 10) / 10,
            apikeyHeader: hasApikey,
            authorizationBearerSource: bearerSource,
            errorName: err && err.name ? String(err.name) : "Error",
          });
        }
        throw err;
      });
  };

  const authStorage = __resolveAuthStorageSync();

  const supabase = createClient(url, key, {
    global: {
      fetch: injectedFetch,
    },
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      flowType: "pkce",
      // PKCE/Hash bewusst in hydrateAuthFromCurrentBrowserUrl — kein Auto-Tausch im Konstruktor
      // (verhindert doppelten Code-Verbrauch + falsche auth_code-URL).
      detectSessionInUrl: false,
      storage: authStorage,
      // Wichtig: kein navigator.locks-basierter Cross-Tab-Lock.
      // Bei mehreren App-Tabs (z. B. localhost-Hub) führt der Default-Lock zu
      // wiederholtem `AbortError: The operation was aborted` in `getSession()`.
      lock: __ttbInProcessLock,
    },
  });

  // expose for quick debugging
  globalThis.__ttb_supabase = supabase;
  globalThis.__ttb_supabase_cfg = cfg;

  __registerAuthStateHub(supabase);

  __originalAuthGetUser = supabase.auth.getUser.bind(supabase.auth);
  supabase.auth.getUser = (jwt) => __cachedAuthGetUser(jwt);

  // Hydrate cache once so first parallel queries after client init use the user JWT (RLS), not only anon.
  // Race: ein hängendes getSession() darf die Client-Erstellung nicht blockieren (sonst Auth-Bootstrap-Timeout).
  // PKCE-OAuth-Callback: kein getSession hier — hydrateAuthFromCurrentBrowserUrl tauscht den Code genau einmal.
  const __initSessionMs = 12000;
  if (!__hasOAuthPkceCodeInBrowserUrl()) {
    try {
      const sessionRes = await Promise.race([
        ttbRunSerializedAuthGetSession(supabase),
        new Promise((_, reject) =>
          setTimeout(
            () =>
              reject(
                Object.assign(new Error("supabase-init-getsession-timeout"), {
                  code: "SUPABASE_INIT_GETSESSION_TIMEOUT",
                }),
              ),
            __initSessionMs,
          ),
        ),
      ]);
      __setSupabaseAuthBearerFromSession(sessionRes?.data?.session);
    } catch (e) {
      try {
        if (
          typeof import.meta !== "undefined" &&
          import.meta.env?.DEV &&
          typeof console !== "undefined" &&
          console.warn
        ) {
          console.warn(
            "auth: supabase client init getSession finished without session",
            {
              reason: String(e?.code || e?.message || e || "unknown"),
            },
          );
        }
      } catch (_) {}
      __supabaseAuthBearerCache = "";
    }
  }

  return supabase;
}

// --- public API -------------------------------------------------------------

export async function getSupabaseClient() {
  const devLog = __debugSupabaseVerbose()
    ? (...a) => {
        console.log(...a);
      }
    : () => {};

  devLog("🔍 [Supabase] getSupabaseClient called");

  const resolvedCfg = __readSupabaseConfig();
  if (globalThis.__ttb_supabase) {
    if (!__supabaseConfigMatches(globalThis.__ttb_supabase_cfg, resolvedCfg)) {
      devLog("♻️ [Supabase] Config changed — recreating client");
      __resetSupabaseClientCache();
    } else {
      devLog(
        "✅ [Supabase] Using cached client from globalThis.__ttb_supabase",
      );
      devLog(
        "🔍 [Supabase] Client has auth:",
        !!(globalThis.__ttb_supabase && globalThis.__ttb_supabase.auth),
      );
      return globalThis.__ttb_supabase;
    }
  }

  if (!_clientPromise) {
    devLog("🔍 [Supabase] Creating new client promise...");
    _clientPromise = __createClient().catch((e) => {
      console.error("❌ [Supabase] Failed to create client:", e);
      _clientPromise = null;
      throw e;
    });
  }

  const client = await _clientPromise;
  devLog("✅ [Supabase] Client created/retrieved");
  devLog("🔍 [Supabase] Client has auth:", !!(client && client.auth));

  return client;
}

// Synchronous version that returns the client if already initialized
// Returns null if not yet initialized (for cases where we can't await)
export function getSupabaseClientSync() {
  if (globalThis.__ttb_supabase) return globalThis.__ttb_supabase;
  return null;
}

export async function getSupabaseUser(options = {}) {
  const force = Boolean(options?.force);
  if (force) {
    invalidateSupabaseUserCache();
    if (!__originalAuthGetUser) {
      await getSupabaseClient();
    }
    if (!__originalAuthGetUser) return null;
    const res = await __originalAuthGetUser();
    if (res?.error) return null;
    const user = res?.data?.user ?? null;
    __supabaseUserCache = {
      user,
      token: __supabaseAuthBearerCache || "",
      ts: Date.now(),
    };
    return user;
  }

  const res = await __cachedAuthGetUser();
  if (res?.error) return null;
  return res?.data?.user ?? null;
}

// -------------------------
// DEV helpers (Phase 1)
// -------------------------

export async function devSupabaseLogin(email, redirectTo) {
  const supabase = await getSupabaseClient();
  const em = String(email || "").trim();
  if (!em) throw new Error("email missing");

  const rt = redirectTo
    ? String(redirectTo)
    : typeof window !== "undefined"
      ? window.location.href
      : "";
  const { error } = await supabase.auth.signInWithOtp({
    email: em,
    options: { emailRedirectTo: rt },
  });
  if (error) throw error;
  return true;
}

export async function devSupabaseLogout() {
  const supabase = await getSupabaseClient();
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
  return true;
}

export async function devSupabaseWhoami() {
  const supabase = await getSupabaseClient();
  const { data: u1, error: e1 } = await supabase.auth.getUser();
  const { data: s1 } = await ttbRunSerializedAuthGetSession(supabase);
  return {
    ok: !e1,
    user: u1?.user || null,
    session: s1?.session || null,
    error: e1 ? e1.message || String(e1) : null,
  };
}

export function getPhase1TenantId() {
  const cfg = __readSupabaseConfig();
  return String(cfg.phase1TenantId || "").trim();
}

export function devSupabaseConfig() {
  const rt = __runtimeEnv();
  const ve = __viteEnv();
  const cfg = __readSupabaseConfig();

  return {
    runtime: {
      has: !!rt,
      SUPABASE_URL: rt?.SUPABASE_URL ? String(rt.SUPABASE_URL) : "",
      SUPABASE_ANON_KEY_present: !!rt?.SUPABASE_ANON_KEY,
      PHASE1_TENANT_ID: rt?.PHASE1_TENANT_ID ? String(rt.PHASE1_TENANT_ID) : "",
    },
    vite: {
      VITE_SUPABASE_URL: ve?.VITE_SUPABASE_URL
        ? String(ve.VITE_SUPABASE_URL)
        : "",
      VITE_SUPABASE_ANON_KEY_present: !!ve?.VITE_SUPABASE_ANON_KEY,
      VITE_PHASE1_TENANT_ID: ve?.VITE_PHASE1_TENANT_ID
        ? String(ve.VITE_PHASE1_TENANT_ID)
        : "",
    },
    resolved: cfg,
  };
}
